/**
 * eLearning TypeScript Interfaces
 * 
 * These types match the Prisma schema structure and are used throughout
 * the eLearning feature for type safety.
 */

// ============================================
// ENUMS
// ============================================

export enum CourseDifficulty {
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
}

export enum CourseStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

export enum QuestionType {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  TRUE_FALSE = 'TRUE_FALSE',
  SHORT_ANSWER = 'SHORT_ANSWER',
}

export enum EnrollmentStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

export enum BadgeType {
  FIRST_COURSE_COMPLETE = 'FIRST_COURSE_COMPLETE',
  PERFECT_SCORE = 'PERFECT_SCORE',
  COURSE_COMPLETE_10 = 'COURSE_COMPLETE_10',
  COURSE_COMPLETE_50 = 'COURSE_COMPLETE_50',
  STREAK_7_DAYS = 'STREAK_7_DAYS',
  STREAK_30_DAYS = 'STREAK_30_DAYS',
  FAST_LEARNER = 'FAST_LEARNER',
  DEDICATED_LEARNER = 'DEDICATED_LEARNER',
}

export enum AchievementType {
  FIRST_MODULE = 'FIRST_MODULE',
  FIRST_COURSE = 'FIRST_COURSE',
  PERFECT_QUIZ = 'PERFECT_QUIZ',
  STREAK_MILESTONE = 'STREAK_MILESTONE',
  POINTS_MILESTONE = 'POINTS_MILESTONE',
}

// ============================================
// CONTENT STRUCTURE (JSON)
// ============================================

/**
 * Module content structure stored as JSON
 * This allows flexible component-based content
 */
export interface ModuleContent {
  slides: Slide[];
}

export interface Slide {
  id: string;
  order: number;
  title?: string;
  components: Component[];
}

export interface Component {
  id: string;
  type: ComponentType;
  order: number;
  content: ComponentContent;
}

export type ComponentType =
  | 'text'
  | 'heading'
  | 'image'
  | 'video'
  | 'columns'
  | 'card'
  | 'knowledge-check'
  | 'resource'
  | 'table'
  | 'accordion'
  | 'callout';

export type ComponentContent =
  | TextComponentContent
  | HeadingComponentContent
  | ImageComponentContent
  | VideoComponentContent
  | ColumnsComponentContent
  | CardComponentContent
  | KnowledgeCheckComponentContent
  | ResourceComponentContent
  | TableComponentContent
  | AccordionComponentContent
  | CalloutComponentContent;

export interface TextComponentContent {
  html: string; // Rich text HTML
}

export interface HeadingComponentContent {
  level: 1 | 2 | 3 | 4 | 5 | 6;
  text: string;
}

export interface ImageComponentContent {
  mediaId: number; // WordPress media ID
  alt: string;
  caption?: string;
  alignment: 'left' | 'center' | 'right';
  size: 'small' | 'medium' | 'large' | 'full';
}

export interface VideoComponentContent {
  youtubeId: string;
  caption?: string;
  autoplay: boolean;
}

export interface ColumnsComponentContent {
  columnCount: 2 | 3;
  columns: Array<{
    components: Component[]; // Nested components
  }>;
}

export interface CardComponentContent {
  title: string;
  text: string;
  imageId?: number; // WordPress media ID
  link?: string;
  linkText?: string;
}

export interface KnowledgeCheckComponentContent {
  question: string;
  type: 'multiple-choice' | 'true-false' | 'short-answer';
  options?: Array<{ text: string; isCorrect: boolean }>;
  correctAnswer?: boolean | string;
  answerKey?: string;
  explanation?: string;
  points: number;
}

export interface ResourceComponentContent {
  mediaId: number; // WordPress media ID
  fileName: string;
  description?: string;
  downloadText: string;
}

export interface TableComponentContent {
  hasHeader: boolean;
  rows: Array<{
    cells: Array<{
      content: string; // HTML or plain text
    }>;
  }>;
}

export interface AccordionComponentContent {
  items: Array<{
    title: string;
    content: string; // HTML or plain text
    defaultExpanded?: boolean;
  }>;
}

export interface CalloutComponentContent {
  type: 'info' | 'warning' | 'error' | 'success';
  title?: string;
  content: string; // HTML or plain text
}

// ============================================
// CORE MODELS
// ============================================

export interface Course {
  id: string;
  title: string;
  slug: string;
  description?: string;
  objectives: string[];
  prerequisites: string[]; // Course IDs
  estimatedTime: number; // Minutes
  difficulty: CourseDifficulty;
  status: CourseStatus;
  accessLevel?: string; // AccessLevel enum
  audiences?: string[];
  topics?: string[];
  clinicalAreas?: string[];
  modules?: Module[];
  createdAt: string;
  updatedAt: string;
}

export interface Module {
  id: string;
  courseId: string;
  title: string;
  slug: string;
  order: number;
  content: ModuleContent; // JSON structure
  estimatedTime: number; // Minutes
  hasQuiz: boolean;
  quiz?: Quiz;
  createdAt: string;
  updatedAt: string;
}

export interface Quiz {
  id: string;
  moduleId: string;
  title: string;
  passingScore: number; // Percentage
  timeLimit?: number; // Minutes (optional)
  questions: QuizQuestion[];
  createdAt: string;
  updatedAt: string;
}

export interface QuizQuestion {
  id: string;
  quizId: string;
  question: string;
  type: QuestionType;
  order: number;
  options?: Array<{ text: string; isCorrect: boolean }>; // For multiple choice
  correctAnswer?: boolean | string; // For true/false or short answer
  answerKey?: string; // For short answer (keywords)
  points: number;
  explanation?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Enrollment {
  id: string;
  userId: string;
  courseId: string;
  course?: Course;
  status: EnrollmentStatus;
  progress: number; // Percentage
  startedAt: string;
  completedAt?: string;
  lastAccessedAt?: string;
  assignedBy?: string; // Admin user ID
  deadline?: string;
  scheduledStart?: string;
  moduleCompletions?: ModuleCompletion[];
  nextModule?: { id: string; title: string; slug: string } | null;
  quizAttempts?: QuizAttempt[];
  createdAt: string;
  updatedAt: string;
}

export interface ModuleCompletion {
  id: string;
  enrollmentId: string;
  moduleId: string;
  completedAt: string;
  timeSpent: number; // Minutes
  attempts: number;
}

export interface QuizQuestionResult {
  questionId: string;
  correct: boolean;
  correctAnswer?: string;
  explanation?: string;
}

export interface QuizAttempt {
  id: string;
  enrollmentId: string;
  quizId: string;
  score: number; // Percentage
  passed: boolean;
  answers: Record<string, any>; // User's answers: { questionId: answer }
  questionResults?: QuizQuestionResult[];
  startedAt: string;
  completedAt?: string;
  timeSpent?: number; // Minutes
}

export interface Certification {
  id: string;
  userId: string;
  courseId: string;
  course?: Course;
  issuedAt: string;
  expiresAt?: string;
  certificateUrl?: string;
  certificateNumber: string;
}

// ============================================
// GAMIFICATION
// ============================================

export interface UserGamification {
  id: string;
  userId: string;
  totalPoints: number;
  currentStreak: number; // Consecutive days
  longestStreak: number;
  lastActivityDate?: string;
  badges?: UserBadge[];
  achievements?: UserAchievement[];
  createdAt: string;
  updatedAt: string;
}

export interface UserBadge {
  id: string;
  userId: string;
  badgeType: BadgeType;
  earnedAt: string;
}

export interface UserAchievement {
  id: string;
  userId: string;
  achievementType: AchievementType;
  earnedAt: string;
}

// ============================================
// FILTERS & INPUTS
// ============================================

export interface CourseFilters {
  search?: string;
  difficulty?: CourseDifficulty;
  status?: CourseStatus;
  audiences?: string[];
  topics?: string[];
  clinicalAreas?: string[];
  accessLevel?: string;
}

export interface CreateCourseInput {
  title: string;
  slug?: string; // Auto-generated if not provided
  description?: string;
  objectives: string[];
  prerequisites?: string[];
  estimatedTime: number;
  difficulty: CourseDifficulty;
  accessLevel?: string;
  audiences?: string[];
  topics?: string[];
  clinicalAreas?: string[];
}

export interface UpdateCourseInput {
  title?: string;
  description?: string;
  objectives?: string[];
  prerequisites?: string[];
  estimatedTime?: number;
  difficulty?: CourseDifficulty;
  status?: CourseStatus;
  accessLevel?: string;
  audiences?: string[];
  topics?: string[];
  clinicalAreas?: string[];
}

export interface CreateModuleInput {
  courseId: string;
  title: string;
  slug?: string;
  order: number;
  content: ModuleContent;
  estimatedTime: number;
}

export interface UpdateModuleInput {
  title?: string;
  slug?: string;
  content?: ModuleContent;
  estimatedTime?: number;
  order?: number;
}

export interface CreateQuizInput {
  moduleId: string;
  title: string;
  passingScore: number;
  timeLimit?: number;
}

export interface CreateQuestionInput {
  quizId: string;
  question: string;
  type: QuestionType;
  order: number;
  options?: Array<{ text: string; isCorrect: boolean }>;
  correctAnswer?: boolean | string;
  answerKey?: string;
  points: number;
  explanation?: string;
}

export interface QuizAnswers {
  [questionId: string]: any; // Answer format depends on question type
}

export interface CourseAssignment {
  courseId: string;
  userId?: string; // Specific user
  emailDomain?: string; // All users with this domain
  groupName?: string; // User group
  assignedBy: string; // Admin user ID
  deadline?: string;
  scheduledStart?: string;
}

// ============================================
// API RESPONSES
// ============================================

export interface UserProgress {
  userId: string;
  email: string;
  enrollments: Enrollment[];
  totalCourses: number;
  completedCourses: number;
  inProgressCourses: number;
}

export interface CourseAnalytics {
  courseId: string;
  totalEnrollments: number;
  completedEnrollments: number;
  averageScore: number;
  averageTimeToComplete: number; // Minutes
  completionRate: number; // Percentage
}

